// giftcard.jsx
import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, Grid, IconButton, Chip } from "@mui/material";
import { toast } from "react-toastify";
import {
  ListProviderGiftCards,
  ListticketType,
} from "../../../Data/GiftsCards";

const GiftCardPayment = ({
  total,
  onPaymentDataChange,
  currentPayment,
  processing,
  disabled,
}) => {
  const [scanCode, setScanCode] = useState("");
  const [giftCards, setGiftCards] = useState([]);
  const [inputQuantity, setInputQuantity] = useState(1);
  const [totalAmount, setTotalAmount] = useState(0);
  const [activeField, setActiveField] = useState("scan");

  const scanInputRef = useRef(null);
  const timeoutRef = useRef(null);
  const lastKeyPressTime = useRef(0);
  const isUpdatingFromParent = useRef(false);

  // Sync with currentPayment when it changes
  useEffect(() => {
    if (currentPayment && currentPayment.giftCards) {
      isUpdatingFromParent.current = true;
      setGiftCards(currentPayment.giftCards);

      // Calculate total from giftCards
      const total = currentPayment.giftCards.reduce(
        (acc, card) => acc + (parseFloat(card.amount) || 0),
        0,
      );
      setTotalAmount(total);

      setTimeout(() => {
        isUpdatingFromParent.current = false;
      }, 50);
    }
  }, [currentPayment]);

  // Auto-focus scan input when component mounts or tab becomes active
  useEffect(() => {
    const timer = setTimeout(() => {
      if (scanInputRef.current) {
        scanInputRef.current.focus();
      }
    }, 200);

    return () => clearTimeout(timer);
  }, []);

  // Parse barcode and extract gift card information
  const parseGiftCardBarcode = (barcode) => {
    if (barcode.length < 11) {
      toast.warning("Code-barres invalide ou trop court.");
      return null;
    }

    const providerCode = barcode.substring(0, 2);
    const typeCode = barcode.substring(2, 4);
    const amountCode = barcode.substring(4, 9);
    const yearCode = barcode.substring(9, 11);
    const year = `20${yearCode}`;

    // Check expiration
    if (parseInt(year) < new Date().getFullYear()) {
      toast.warning(`Ticket expiré depuis l'année ${year}`);
      return null;
    }

    const provider = ListProviderGiftCards.find((p) => p.code === providerCode);
    const ticketType = ListticketType.find((t) => t.code === typeCode);

    if (!provider) {
      toast.warning("Fournisseur de ticket non reconnu");
      return null;
    }

    if (!ticketType) {
      toast.warning("Type de ticket non reconnu");
      return null;
    }

    // Calculate amount (amount from barcode is in thousandths)
    const rawAmount = (parseInt(amountCode, 10) / 1000).toFixed(3);
    const netAmount =
      parseFloat(rawAmount) - parseFloat(rawAmount) * (provider.taux / 100);

    return {
      provider: provider.name,
      providerCode: provider.code,
      type: ticketType.name,
      typeCode: ticketType.code,
      rawAmount: parseFloat(rawAmount),
      amount: parseFloat(netAmount.toFixed(3)),
      year: year,
      barcode: barcode,
      quantity: inputQuantity,
      id: Date.now() + Math.random().toString(36).substr(2, 9),
    };
  };

  // Handle barcode scan
  const handleScan = (e) => {
    const value = e.target.value;
    const cleanedValue = value.replace(/[\r\n\t]+/g, "").trim();
    setScanCode(cleanedValue);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    const now = Date.now();
    const timeDiff = now - lastKeyPressTime.current;
    lastKeyPressTime.current = now;

    // Detect if it's a scanner (fast input) or manual typing
    const isScannerInput = timeDiff < 50;

    timeoutRef.current = setTimeout(
      () => {
        if (cleanedValue !== "") {
          processScannedCode(cleanedValue);
        }
      },
      isScannerInput ? 100 : 2000, // Shorter delay for scanner, longer for manual typing
    );
  };

  const processScannedCode = (barcode) => {
    const parsedCard = parseGiftCardBarcode(barcode);

    if (!parsedCard) {
      setScanCode("");
      scanInputRef.current?.focus();
      return;
    }

    // Check if this exact card was already scanned
    const existingCardIndex = giftCards.findIndex(
      (card) => card.barcode === barcode,
    );

    let updatedGiftCards;

    if (existingCardIndex === -1) {
      // New card - add to list
      updatedGiftCards = [...giftCards, parsedCard];
    } else {
      // Card already exists - increase quantity
      updatedGiftCards = [...giftCards];
      updatedGiftCards[existingCardIndex].quantity += inputQuantity;
      updatedGiftCards[existingCardIndex].amount = parseFloat(
        (
          updatedGiftCards[existingCardIndex].amount + parsedCard.amount
        ).toFixed(3),
      );
    }

    updateGiftCards(updatedGiftCards);
    setScanCode("");
    scanInputRef.current?.focus();
  };

  const updateGiftCards = (updatedGiftCards) => {
    setGiftCards(updatedGiftCards);

    // Calculate total amount
    const newTotal = updatedGiftCards.reduce(
      (acc, card) => acc + (parseFloat(card.amount) || 0),
      0,
    );

    setTotalAmount(newTotal);

    // Notify parent component
    if (!isUpdatingFromParent.current) {
      onPaymentDataChange(
        {
          amount: newTotal,
          rendu: 0,
          giftCards: updatedGiftCards,
          method: "gift_card",
        },
        "gift_card",
      );
    }
  };

  const removeGiftCard = (index) => {
    const updatedGiftCards = giftCards.filter((_, i) => i !== index);
    updateGiftCards(updatedGiftCards);
  };

  const adjustQuantity = (index, action) => {
    const updatedGiftCards = [...giftCards];

    if (action === "plus") {
      updatedGiftCards[index].quantity += 1;
      updatedGiftCards[index].amount = parseFloat(
        (
          updatedGiftCards[index].amount +
          updatedGiftCards[index].rawAmount *
            (1 -
              (ListProviderGiftCards.find(
                (p) => p.name === updatedGiftCards[index].provider,
              )?.taux || 10) /
                100)
        ).toFixed(3),
      );
    } else if (action === "minus") {
      const newQuantity = updatedGiftCards[index].quantity - 1;

      if (newQuantity <= 0) {
        removeGiftCard(index);
        return;
      } else {
        updatedGiftCards[index].quantity = newQuantity;
        updatedGiftCards[index].amount = parseFloat(
          (
            updatedGiftCards[index].amount -
            updatedGiftCards[index].rawAmount *
              (1 -
                (ListProviderGiftCards.find(
                  (p) => p.name === updatedGiftCards[index].provider,
                )?.taux || 10) /
                  100)
          ).toFixed(3),
        );
      }
    }

    updateGiftCards(updatedGiftCards);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (scanCode) {
        processScannedCode(scanCode);
      }
    }
  };

  return (
    <Box sx={{ p: 1 }}>
      {/* Scan Input Section */}
      <Box
        mb={2}
        onClick={() => {
          setActiveField("scan");
          scanInputRef.current?.focus();
          window.dispatchEvent(
            new CustomEvent("virtualKeyboardFocus", {
              detail: { method: "gift_card", input: "scan" },
            }),
          );
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: "bold" }}>
          Scanner le code-barres
        </Typography>
        <Paper
          component="div"
          sx={{
            p: 0.5,
            border:
              activeField === "scan" ? "2px solid #9c27b0" : "1px solid #ccc",
            outline: "none",
            bgcolor: activeField === "scan" ? "#f3e5f5" : "white",
            display: "flex",
            alignItems: "center",
          }}
        >
          <input
            ref={scanInputRef}
            type="text"
            value={scanCode}
            onChange={handleScan}
            onKeyDown={handleKeyDown}
            placeholder="Scan nez le code-barres..."
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              padding: "10px",
              fontSize: "1rem",
              background: "transparent",
            }}
            disabled={processing || disabled}
          />
          <Box sx={{ px: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              size="small"
              label={`x${inputQuantity}`}
              color="secondary"
              onDelete={() => setInputQuantity((prev) => Math.max(1, prev - 1))}
              deleteIcon={<span>-</span>}
            />
            <Chip
              size="small"
              label="+"
              onClick={() => setInputQuantity((prev) => prev + 1)}
            />
          </Box>
        </Paper>
      </Box>

      {/* Gift Cards List */}
      {giftCards.length > 0 && (
        <Box sx={{ mt: 2, maxHeight: "300px", overflowY: "auto" }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
            Tickets scannés:
          </Typography>

          {giftCards.map((card, index) => (
            <Paper
              key={card.id}
              sx={{
                p: 1.5,
                mb: 1,
                bgcolor: "#fafafa",
                borderLeft: "4px solid #9c27b0",
              }}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    Fournisseur
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {card.provider}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    Type
                  </Typography>
                  <Typography variant="body2">{card.type}</Typography>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="caption" color="textSecondary">
                    Qté
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => adjustQuantity(index, "minus")}
                      disabled={processing}
                    >
                      <small>-</small>
                    </IconButton>
                    <Typography>{card.quantity}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => adjustQuantity(index, "plus")}
                      disabled={processing}
                    >
                      <small>+</small>
                    </IconButton>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={2}>
                  <Typography variant="caption" color="textSecondary">
                    Montant
                  </Typography>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {card.amount.toFixed(3)} TND
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => removeGiftCard(index)}
                    disabled={processing}
                  >
                    <small>🗑️</small>
                  </IconButton>
                </Grid>
              </Grid>
            </Paper>
          ))}

          {/* Total Amount */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mt: 2, pt: 1, borderTop: "1px dashed #ccc" }}
          >
            <Typography variant="subtitle1" fontWeight="bold">
              Total Tickets:
            </Typography>
            <Typography variant="h6" color="secondary" fontWeight="bold">
              {totalAmount.toFixed(3)} TND
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default GiftCardPayment;
